import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Title,
  Text,
  Table,
  NumberInput,
  Button,
  Stack,
  Group,
  Badge,
  Alert,
  Loader,
  Tooltip,
  Textarea,
  Modal,
  ActionIcon,
} from '@mantine/core';
import { IconDeviceFloppy, IconAlertCircle, IconCheck, IconNotes, IconEdit } from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { evaluationScoresApi } from '../../../api/evaluationScores';
import { formatCurrency } from '../../../utils/currency';
import { notifications } from '@mantine/notifications';
import { EvaluationCriteria } from '../../../api/evaluationCriteria';
import { BidSubmission } from '../../../api/bidSubmissions';
import { TechnicalEvaluation } from '../../../api/technicalEvaluation';
import { Tender } from '../../../api/tenders';
import { SaveScoresDto, BulkSaveScoresDto } from '../../../api/evaluationScores';
import { useUpdateEvaluationNotes } from '../hooks/useEvaluationScores';
import { useCompleteEvaluation } from '../hooks/useEvaluationReports';

interface OverallEvaluationTabProps {
  tenderId: string;
  tender: Tender | undefined;
  bidSubmissions: BidSubmission[];
  technicalEvaluations: TechnicalEvaluation[];
  evaluationCriteria: EvaluationCriteria[];
  loadingCriteria: boolean;
  scoreData: Record<string, Record<string, number>>;
  setScoreData: React.Dispatch<React.SetStateAction<Record<string, Record<string, number>>>>;
  saveScores: any;
  bulkSaveScores: any;
  navigate: (path: string) => void;
}

export function OverallEvaluationTab({
  tenderId,
  tender,
  bidSubmissions,
  technicalEvaluations,
  evaluationCriteria,
  loadingCriteria,
  scoreData,
  setScoreData,
  saveScores,
  bulkSaveScores,
  navigate,
}: OverallEvaluationTabProps) {
  const updateEvaluationNotes = useUpdateEvaluationNotes();
  const completeEvaluation = useCompleteEvaluation();
  const [notesModalOpened, setNotesModalOpened] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<BidSubmission | null>(null);
  const [notesValue, setNotesValue] = useState('');

  // Filter eligible bid submissions (qualified + tech approved)
  const eligibleSubmissions = useMemo(() => {
    return bidSubmissions.filter((submission) => {
      // Must be qualified
      if (submission.qualificationStatus !== 'QUALIFIED') return false;

      // Must have approved technical evaluation
      const techEval = technicalEvaluations.find(
        (te) => te.bidSubmissionId === submission.id && te.status === 'APPROVED'
      );
      return !!techEval;
    });
  }, [bidSubmissions, technicalEvaluations]);

  // Separate criteria by type
  const financialCriteria = useMemo(
    () => evaluationCriteria.filter((c) => c.criteriaType === 'FINANCIAL'),
    [evaluationCriteria]
  );
  const generalCriteria = useMemo(
    () => evaluationCriteria.filter((c) => c.criteriaType === 'GENERAL'),
    [evaluationCriteria]
  );

  // Calculate lowest price for financial score calculation
  const lowestPrice = useMemo(() => {
    if (eligibleSubmissions.length === 0) return 0;
    const prices = eligibleSubmissions.map((s) => Number(s.totalPrice));
    return Math.min(...prices);
  }, [eligibleSubmissions]);

  // Note: Total general weightage calculation removed - scores don't need to sum to weightage

  // Load scores for each submission using useQueries
  const submissionIds = useMemo(
    () => eligibleSubmissions.map((s) => s.id),
    [eligibleSubmissions.map((s) => s.id).join(',')]
  );

  const scoreQueries = useQueries({
    queries: submissionIds.map((submissionId) => ({
      queryKey: ['evaluationScores', tenderId, submissionId],
      queryFn: async () => {
        const scores = await evaluationScoresApi.getBidSubmissionScores(tenderId, submissionId);
        return { submissionId, scores };
      },
      enabled: !!tenderId && !!submissionId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    })),
  });

  // Initialize score data from loaded scores
  useEffect(() => {
    const newScoreData: Record<string, Record<string, number>> = {};

    scoreQueries.forEach((query) => {
      if (query.data) {
        const { submissionId, scores } = query.data;
        const submissionScores: Record<string, number> = {};
        scores.forEach((score) => {
          submissionScores[score.criteriaId] = score.score;
        });
        newScoreData[submissionId] = submissionScores;
      }
    });

    // Update score data if there are changes
    const hasChanges = Object.keys(newScoreData).some(
      (submissionId) =>
        JSON.stringify(newScoreData[submissionId]) !== JSON.stringify(scoreData[submissionId])
    );

    if (hasChanges && Object.keys(newScoreData).length > 0) {
      setScoreData((prev) => ({ ...prev, ...newScoreData }));
    }
  }, [scoreQueries.map((q) => q.dataUpdatedAt || 0).join(',')]);

  // Calculate financial score
  const calculateFinancialScore = (
    submission: BidSubmission,
    criteria: EvaluationCriteria
  ): number => {
    if (Number(submission.totalPrice) === 0 || lowestPrice === 0) return 0;
    return (lowestPrice / Number(submission.totalPrice)) * Number(criteria.weightage);
  };

  // Validate general scores for a submission (only check individual score ranges, not sum)
  const validateGeneralScores = (submissionId: string): { valid: boolean; error?: string } => {
    const submissionScores = scoreData[submissionId] || {};

    // Validate each general score is within range (0 to weightage)
    for (const criteria of generalCriteria) {
      const score = submissionScores[criteria.id] || 0;
      if (score < 0 || score > Number(criteria.weightage)) {
        return {
          valid: false,
          error: `Score for ${criteria.criteriaName} must be between 0 and ${criteria.weightage}`,
        };
      }
    }

    return { valid: true };
  };

  // Update score in state
  const updateScore = (submissionId: string, criteriaId: string, score: number) => {
    setScoreData((prev) => ({
      ...prev,
      [submissionId]: {
        ...(prev[submissionId] || {}),
        [criteriaId]: score,
      },
    }));
  };

  // Calculate total score for a submission
  const calculateTotalScore = (submission: BidSubmission): number => {
    const submissionScores = scoreData[submission.id] || {};
    let total = 0;

    // Add financial scores (auto-calculated)
    financialCriteria.forEach((criteria) => {
      total += calculateFinancialScore(submission, criteria);
    });

    // Add general scores (from state)
    generalCriteria.forEach((criteria) => {
      total += submissionScores[criteria.id] || 0;
    });

    return total;
  };

  // Save scores for a single submission
  const handleSaveScores = async (submission: BidSubmission) => {
    const validation = validateGeneralScores(submission.id);
    if (!validation.valid) {
      notifications.show({
        title: 'Validation Error',
        message: validation.error,
        color: 'red',
      });
      return;
    }

    const submissionScores = scoreData[submission.id] || {};
    const scoresToSave: SaveScoresDto = {
      scores: evaluationCriteria.map((criteria) => {
        let score: number;
        if (criteria.criteriaType === 'FINANCIAL') {
          // Auto-calculate financial score
          score = calculateFinancialScore(submission, criteria);
        } else {
          // Use saved general score
          score = submissionScores[criteria.id] || 0;
        }

        return {
          criteriaId: criteria.id,
          score,
        };
      }),
    };

    try {
      await saveScores.mutateAsync({
        tenderId,
        bidSubmissionId: submission.id,
        data: scoresToSave,
      });
    } catch (error) {
      // Error handled by mutation
    }
  };

  // Bulk save all scores
  const handleBulkSave = async () => {
    // Validate all submissions
    const validationErrors: string[] = [];
    eligibleSubmissions.forEach((submission) => {
      const validation = validateGeneralScores(submission.id);
      if (!validation.valid) {
        validationErrors.push(`${submission.companyName}: ${validation.error}`);
      }
    });

    if (validationErrors.length > 0) {
      notifications.show({
        title: 'Validation Error',
        message: validationErrors.join('\n'),
        color: 'red',
      });
      return;
    }

    const bulkData: BulkSaveScoresDto = {
      submissions: eligibleSubmissions.map((submission) => {
        const submissionScores = scoreData[submission.id] || {};
        return {
          bidSubmissionId: submission.id,
          scores: evaluationCriteria.map((criteria) => {
            let score: number;
            if (criteria.criteriaType === 'FINANCIAL') {
              score = calculateFinancialScore(submission, criteria);
            } else {
              score = submissionScores[criteria.id] || 0;
            }

            return {
              criteriaId: criteria.id,
              score,
            };
          }),
        };
      }),
    };

    try {
      await bulkSaveScores.mutateAsync({
        tenderId,
        data: bulkData,
      });
    } catch (error) {
      // Error handled by mutation
    }
  };

  if (loadingCriteria) {
    return (
      <Card shadow="sm" p="lg">
        <Loader size="md" />
      </Card>
    );
  }

  if (evaluationCriteria.length === 0) {
    return (
      <Card shadow="sm" p="lg">
        <Alert color="blue" title="No Evaluation Criteria">
          Please define evaluation criteria for this tender before proceeding with evaluation.
        </Alert>
      </Card>
    );
  }

  if (eligibleSubmissions.length === 0) {
    return (
      <Card shadow="sm" p="lg">
        <Alert color="blue" title="No Eligible Bid Submissions">
          No qualified bid submissions with approved technical evaluations found. Please qualify bid
          submissions and complete technical evaluations first.
        </Alert>
      </Card>
    );
  }

  // Check if any scores are loading
  const isLoadingScores = scoreQueries.some((q) => q.isLoading);

  return (
    <Stack gap="md">
      <Card shadow="sm" p="lg">
        <Group justify="space-between" mb="md">
          <Title order={3}>Overall Evaluation</Title>
          <Group gap="xs">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleBulkSave}
              loading={bulkSaveScores.isPending}
              disabled={isLoadingScores}
            >
              Save All Scores
            </Button>
            {(tender?.stage === 'FIN_EVAL' || tender?.stage === 'TECH_EVAL') && (
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={() => completeEvaluation.mutate(tenderId)}
                loading={completeEvaluation.isPending}
                color="green"
              >
                Complete Evaluation
              </Button>
            )}
          </Group>
        </Group>

        {isLoadingScores ? (
          <Loader size="md" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Company</Table.Th>
                  <Table.Th>Total Price</Table.Th>
                  {evaluationCriteria.map((criteria) => (
                    <Table.Th key={criteria.id}>
                      <div>
                        <Text size="sm" fw={500}>
                          {criteria.criteriaName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {criteria.criteriaType === 'FINANCIAL' ? 'Financial' : 'General'} (
                          {Number(criteria.weightage)}%)
                        </Text>
                      </div>
                    </Table.Th>
                  ))}
                  <Table.Th>Total Score</Table.Th>
                  <Table.Th>Notes</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {eligibleSubmissions.map((submission) => {
                  const validation = validateGeneralScores(submission.id);
                  const totalScore = calculateTotalScore(submission);
                  const submissionScores = scoreData[submission.id] || {};

                  return (
                    <Table.Tr key={submission.id}>
                      <Table.Td>
                        <Text fw={500}>{submission.companyName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {formatCurrency(Number(submission.totalPrice), tender?.currency || 'MVR')}
                        </Text>
                      </Table.Td>
                      {evaluationCriteria.map((criteria) => {
                        if (criteria.criteriaType === 'FINANCIAL') {
                          // Financial criteria - read-only, auto-calculated
                          const score = calculateFinancialScore(submission, criteria);
                          return (
                            <Table.Td key={criteria.id}>
                              <Tooltip label="Auto-calculated based on price">
                                <Badge size="sm" color="blue" variant="light">
                                  {score.toFixed(2)}
                                </Badge>
                              </Tooltip>
                            </Table.Td>
                          );
                        } else {
                          // General criteria - editable
                          const score = submissionScores[criteria.id] || 0;
                          return (
                            <Table.Td key={criteria.id}>
                              <NumberInput
                                value={score}
                                onChange={(value) =>
                                  updateScore(submission.id, criteria.id, typeof value === 'number' ? value : Number(value) || 0)
                                }
                                min={0}
                                max={Number(criteria.weightage)}
                                step={0.01}
                                decimalScale={2}
                                style={{ width: '80px' }}
                              />
                            </Table.Td>
                          );
                        }
                      })}
                      <Table.Td>
                        <Badge size="sm" color="green" variant="light">
                          {totalScore.toFixed(2)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {submission.evaluationNotes && (
                            <Tooltip label={submission.evaluationNotes} withArrow>
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setNotesValue(submission.evaluationNotes || '');
                                  setNotesModalOpened(true);
                                }}
                              >
                                <IconNotes size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={submission.evaluationNotes ? <IconEdit size={14} /> : <IconNotes size={14} />}
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setNotesValue(submission.evaluationNotes || '');
                              setNotesModalOpened(true);
                            }}
                          >
                            {submission.evaluationNotes ? 'Edit Notes' : 'Add Notes'}
                          </Button>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconDeviceFloppy size={14} />}
                            onClick={() => handleSaveScores(submission)}
                            loading={saveScores.isPending}
                            disabled={!validation.valid}
                          >
                            Save
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() =>
                              navigate(`/tenders/${tenderId}/bid-submissions/${submission.id}`)
                            }
                          >
                            View
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        )}

        {eligibleSubmissions.some((s) => {
          const validation = validateGeneralScores(s.id);
          return !validation.valid;
        }) && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" mt="md">
            <Text size="sm" fw={500} mb="xs">
              Validation Warnings:
            </Text>
            <Stack gap="xs">
              {eligibleSubmissions.map((submission) => {
                const validation = validateGeneralScores(submission.id);
                if (!validation.valid) {
                  return (
                    <Text key={submission.id} size="xs">
                      <strong>{submission.companyName}:</strong> {validation.error}
                    </Text>
                  );
                }
                return null;
              })}
            </Stack>
          </Alert>
        )}

        <Text size="sm" c="dimmed" mt="md">
          <strong>Note:</strong> Financial criteria scores are auto-calculated based on price. General
          criteria scores can be set independently and don't need to sum to a specific total.
        </Text>
      </Card>

      {/* Evaluation Notes Section */}
      <Card shadow="sm" p="lg">
        <Group justify="space-between" mb="md">
          <Title order={3}>Evaluation Notes</Title>
        </Group>
        <Text size="sm" c="dimmed" mb="md">
          Justifications and remarks for each bid submission's overall evaluation.
        </Text>

        <Stack gap="md">
          {eligibleSubmissions.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No eligible bid submissions to display notes for.
            </Text>
          ) : (
            eligibleSubmissions.map((submission) => (
              <Card key={submission.id} shadow="xs" p="md" withBorder>
                <Group justify="space-between" mb="sm">
                  <Text fw={500}>{submission.companyName}</Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={submission.evaluationNotes ? <IconEdit size={14} /> : <IconNotes size={14} />}
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setNotesValue(submission.evaluationNotes || '');
                      setNotesModalOpened(true);
                    }}
                  >
                    {submission.evaluationNotes ? 'Edit Notes' : 'Add Notes'}
                  </Button>
                </Group>
                {submission.evaluationNotes ? (
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {submission.evaluationNotes}
                  </Text>
                ) : (
                  <Text size="sm" c="dimmed" fs="italic">
                    No evaluation notes added yet. Click "Add Notes" to add justifications and remarks.
                  </Text>
                )}
              </Card>
            ))
          )}
        </Stack>
      </Card>

      {/* Evaluation Notes Modal */}
      <Modal
        opened={notesModalOpened}
        onClose={() => {
          setNotesModalOpened(false);
          setSelectedSubmission(null);
          setNotesValue('');
        }}
        title={`Evaluation Notes - ${selectedSubmission?.companyName}`}
        size="lg"
      >
        <Stack gap="md">
          <Textarea
            label="Evaluation Notes & Justifications"
            placeholder="Enter notes, justifications, and remarks for this bid submission's overall evaluation..."
            value={notesValue}
            onChange={(e) => setNotesValue(e.target.value)}
            rows={8}
            autosize
            minRows={8}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setNotesModalOpened(false);
                setSelectedSubmission(null);
                setNotesValue('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedSubmission) {
                  await updateEvaluationNotes.mutateAsync({
                    tenderId,
                    bidSubmissionId: selectedSubmission.id,
                    evaluationNotes: notesValue,
                  });
                  setNotesModalOpened(false);
                  setSelectedSubmission(null);
                  setNotesValue('');
                }
              }}
              loading={updateEvaluationNotes.isPending}
            >
              Save Notes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

